import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { DateProvider } from "./contexts/DateContext";
import { TimezoneProvider } from "./contexts/TimezoneContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SupportModeProvider } from "./contexts/SupportModeContext";
import { TrainingMonitoringConsentProvider } from "./contexts/TrainingMonitoringConsentProvider";
import { TrainingLaunchDisclosureProvider } from "./contexts/TrainingLaunchDisclosureProvider";
// K108-W — global return-to-OptiBay completion prompt (inside Router for useLocation).
import { TrainingCompletionReturnPromptProvider } from "./contexts/TrainingCompletionReturnPromptProvider";
// K108-Z — Companion token-rehydrate bridge (SW-restart reconnect). Renders nothing.
import { CompanionRehydrateBridge } from "./components/training/CompanionRehydrateBridge";
import { IdleSignOutGuard } from "./components/auth/IdleSignOutGuard";
import { MobileTrainingReturnPrompt } from "./components/training/MobileTrainingReturnPrompt";
import { TutorialProvider } from "./contexts/TutorialContext";
import { MarketingModeProvider } from "./contexts/MarketingModeContext";
import { ReminderTriggerProvider } from "./components/reminders/ReminderTriggerProvider";
import { ScopeExpansionHandler } from "./components/optimizer/ScopeExpansionHandler";
import { UpdateNotification } from "./components/layout/UpdateNotification";
import { BillingGate } from "./components/billing/BillingGate";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { SchoolRoute } from "./components/auth/SchoolRoute";
import { InternalAdminRoute } from "./components/auth/InternalAdminRoute";
import { SupportOperatorRoute } from '@/components/auth/SupportOperatorRoute';
import BetaControlPanel from "./pages/admin/BetaControlPanel";
import AiIntakeLab from "./pages/AiIntakeLab";
import { AddonSuggestionProvider } from "@/contexts/AddonSuggestionContext";
import AdminSchoolCreator from "./pages/admin/AdminSchoolCreator";
import AccountRequestsAdmin from "./pages/admin/AccountRequestsAdmin";
import RequestAccount from "./pages/RequestAccount";
import ReferenceRespond from "./pages/ReferenceRespond";
import Dashboard from "./pages/Dashboard";
import ShopProfile from "./pages/ShopProfile";
import Technicians from "./pages/Technicians";
import TechnicianEditor from "./pages/TechnicianEditor";
import ShopFloor from "./pages/ShopFloor";
import FacilityEditor from "./pages/FacilityEditor";
import SyncVehicleData from "./pages/SyncVehicleData";
import ImportVehicleCatalog from "./pages/ImportVehicleCatalog";
import ImportGeoVehicles from "./pages/ImportGeoVehicles";
import ImportJobs from "./pages/ImportJobs";
import ImportSources from "./pages/ImportSources";
import ImportRuns from "./pages/ImportRuns";
import AssignmentDebug from "./pages/AssignmentDebug";
import Birdseye from "./pages/Birdseye";
import DeletedTickets from "./pages/DeletedTickets";
import CompletedTickets from "./pages/CompletedTickets";
import Analytics from "./pages/Analytics";
import TeamManagement from "./pages/TeamManagement";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import OnboardShop from "./pages/OnboardShop";
import Onboard from "./pages/Onboard";
import OnboardSetup from "./pages/OnboardSetup";
import OnboardSetupImport from "./pages/OnboardSetupImport";
import OnboardSetupImportPreview from "./pages/OnboardSetupImportPreview";
import OnboardSetupImportInferences from "./pages/OnboardSetupImportInferences";
import OnboardSetupImportWizard from "./pages/OnboardSetupImportWizard";
import AcceptInvitation from "./pages/AcceptInvitation";
import JoinTeam from "./pages/JoinTeam";
import JoinTeamAccept from "./pages/JoinTeamAccept";
import AuthConfirm from "./pages/AuthConfirm";
import AlphaTesters from "./pages/AlphaTesters";
import AccountSettings from "./pages/AccountSettings";
import PurchaseOrderListPage from "./components/purchasing/PurchaseOrderListPage";
import PurchaseOrderDetailPage from "./components/purchasing/PurchaseOrderDetailPage";
import CannedJobsPage from "./pages/CannedJobsPage";
import InventoryPage from "./pages/InventoryPage";
import InvoicesList from "./pages/InvoicesList";
import InvoiceDetail from "./pages/InvoiceDetail";
import CreateTicket from "./pages/CreateTicket";
import SpecialtyBays from "./pages/SpecialtyBays";
import BetaLeads from "./pages/BetaLeads";
import FeatureFlagsAdmin from "./pages/FeatureFlagsAdmin";
import SupportOperatorsAdmin from "./pages/admin/SupportOperatorsAdmin";
import PrivacyPosture from "./pages/admin/PrivacyPosture";
import DemoSeeder from "./pages/admin/DemoSeeder";
import TestDataCleanup from "./pages/admin/TestDataCleanup";
import FohQueue from "./pages/FohQueue";
import ScheduleConflicts from "./pages/ScheduleConflicts";
import { ScheduleConflictViewerRoute } from "./components/auth/ScheduleConflictViewerRoute";
import BetaInvite from "./pages/BetaInvite";
import BetaComplete from "./pages/BetaComplete";
import BillingSuccess from "./pages/BillingSuccess";
import BetaSignAgreement from "./pages/BetaSignAgreement";
import BetaLegalReview from "./pages/BetaLegalReview";
import UserGuide from "./pages/UserGuide";
import AddonSuggestionSettings from "./pages/AddonSuggestionSettings";
import VehicleDuplicates from "./pages/VehicleDuplicates";
import BugReports from "./pages/BugReports";
import BugReportDetail from "./pages/BugReportDetail";
import SupportShops from "./pages/SupportShops";
import SupportDashboard from "./pages/SupportDashboard";
// Core-100 R5 CORE-BJ / OD-49: read-only view of a shop that approved support access.
import SupportGrantedShopView from "./pages/SupportGrantedShopView";
import TechnicianPassport from "./pages/TechnicianPassport";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import PassportTerms from "./pages/legal/PassportTerms";
import BetaAgreement from "./pages/legal/BetaAgreement";
import AdvisorPassport from "./pages/AdvisorPassport";
import ManagerPassport from "./pages/ManagerPassport";
import PassportHome from "./pages/PassportHome";
import ConnectPage from "./pages/ConnectPage";
import TalentProfileView from "./pages/TalentProfileView";
import EnterpriseTalent from "./pages/EnterpriseTalent";
import EnterpriseTalentAnalytics from "./pages/EnterpriseTalentAnalytics";
import EnterpriseTraining from "./pages/EnterpriseTraining";
import EnterpriseTalentSuggestions from "./pages/EnterpriseTalentSuggestions";
import EnterpriseIntelligenceGovernance from "./pages/EnterpriseIntelligenceGovernance";
import DecoupledUsers from "./pages/DecoupledUsers";
import BayRulesReview from "./pages/BayRulesReview";
import OperationalIntegrity from "./pages/OperationalIntegrity";
import TrainingCatalogAdmin from "./pages/TrainingCatalogAdmin";
// TCX-7 — Training domain-match policy admin (internal-admin surface).
import TrainingDomainPolicyAdmin from "./pages/TrainingDomainPolicyAdmin";
import DiagnosticFlowAdmin from "./pages/DiagnosticFlowAdmin";
import OptiQueueSettings from "./pages/OptiQueueSettings";
import OptiQueueBookingPage from "./pages/OptiQueueBookingPage";
import PayrollReports from "./pages/PayrollReports";
import DviCustomerView from "./pages/DviCustomerView";
import OrgView from "./pages/OrgView";
import OrgIntelligence from "./pages/OrgIntelligence";
import OrgRecommendations from "./pages/OrgRecommendations";
import FlooNetworkMap from "./pages/admin/FlooNetworkMap";
import OptiNetworkDashboard from "./pages/admin/OptiNetworkDashboard";
import QualityCommandCenter from "./pages/admin/QualityCommandCenter";
import QualityRunDetail from "./pages/admin/quality/RunDetail";
import RegionDashboard from "./pages/RegionDashboard";
import DistrictDashboard from "./pages/DistrictDashboard";
import OptiAssets from "./pages/OptiAssets";
import DviIntelligenceDashboard from "./pages/DviIntelligenceDashboard";
import DviLearningMetricsDashboard from "./pages/DviLearningMetricsDashboard";
import Customers from "./pages/Customers";
import { PageWithTopBar } from "./components/layout/PageWithTopBar";
import CustomerProfile from "./pages/CustomerProfile";
import OptiBoard from "./pages/OptiBoard";
import ChronicleRouter from "./chronicle/ChronicleRouter";
import InspectionTemplates from "./pages/InspectionTemplates";
import CustomerInbox from "./pages/CustomerInbox";
import CreatorAuth from "./pages/creator/CreatorAuth";
import CreatorDashboard from "./pages/creator/CreatorDashboard";
import CreatorProtectedRoute from "./components/creator/CreatorProtectedRoute";
import SchoolPortal from "./pages/school/SchoolPortal";
import SchoolDashboard from "./pages/school/SchoolDashboard";
import ProgramsManager from "./pages/school/ProgramsManager";
import CohortsManager from "./pages/school/CohortsManager";
import JoinRequestsQueue from "./pages/school/JoinRequestsQueue";
import StudentVerificationsManager from "./pages/school/StudentVerificationsManager";
import StaffManager from "./pages/school/StaffManager";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import AttachEvidence from "./pages/AttachEvidence";
import { TutorialRouteWatcher } from "@/components/onboarding/TutorialRouteWatcher";
import { GlobalTutorialBar } from "@/components/onboarding/GlobalTutorialBar";
import { ReportIssueButton } from "@/components/bugs/ReportIssueButton";
import { TimerReviewHost } from "@/components/tickets/TimerReviewHost";

import { SupportModeBanner } from "./components/support/SupportModeBanner";
import { SupportModeNavigationTracker } from "./hooks/useSupportAudit";
import { FeatureFlagsDebug } from "./components/debug/FeatureFlagsDebug";
import { StormforgeOverlay } from "./components/stormforge/StormforgeOverlay";
import { StormforgeListener } from "./components/stormforge/StormforgeListener";
import { ExposureRouteGate } from "./components/access/ExposureRouteGate";
import { USOnlyNotice } from "./components/compliance/USOnlyNotice";
import { BetaConfidentialityOverlay } from "./components/beta/BetaConfidentialityOverlay";
import { EveVoiceShell } from "./components/eve/EveVoiceShell";
import { FloatingControlsReserveHost } from "./lib/layout/floatingControlsReserve";
// FLOYD 2026-10-26 (DEFECT 1): "OptiBay was updated — reload" when this page runs a superseded build.
import { AppUpdateRequiredBanner } from "./components/app/AppUpdateRequired";
// Batch 10: cap React Query retries on 5xx responses so a server-side
// failure (PostgREST 500, missing column, RLS recursion, etc.) does
// not produce an exponentially-growing flood of identical requests
// in the console while the page sits open. Client-side errors (4xx)
// still get the default single retry per the supabase-js/React Query
// convention. The actual error body is captured by the per-page
// logSupabaseError helpers (Birdseye.tsx, Dashboard.tsx) so the
// operator can still triage without the retry storm.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // PostgREST / supabase-js surfaces HTTP status under various
        // shapes depending on whether it came from the JSON RPC path,
        // the auto-generated REST path, or an Edge Function. Probe
        // all three so a 5xx response is caught regardless of source.
        const e = (error ?? {}) as { status?: number; code?: string; message?: string };
        const status = typeof e.status === 'number' ? e.status : Number(e.code) || 0;
        if (status >= 500 && status < 600) return false;
        return failureCount < 1;
      },
    },
  },
});

/**
 * EveVoiceShellWithActiveShop
 * Bridge from AuthContext's canonical `activeShop` to EveVoiceShell. The
 * shell is mounted globally and lives outside any per-shop route, so the
 * only stable source for advisory shop scope is AuthContext. When no
 * shop is active (e.g. signed-out, passport-only, or admin without an
 * activated tenant) the prop stays undefined and eve-voice falls back to
 * the user's first active membership (its pre-existing behavior).
 */
const EveVoiceShellWithActiveShop = () => {
  const { activeShop } = useAuth();
  return <EveVoiceShell shopId={activeShop?.id} />;
};

/**
 * BootRouteReset
 * One-time boot guard: if the preview iframe persisted `/passport` from a prior
 * session but the current user is not in passport mode (i.e. they have shop
 * memberships), redirect them to `/` so the Dashboard is the canonical home.
 * Manual navigation to `/passport` (e.g. self-view) is not affected — this
 * only runs once at mount.
 */
const BootRouteReset = () => {
  const { user, userShops, isPassportMode, isLoading, isShopDataLoading } = useAuth();
  useEffect(() => {
    if (isLoading || isShopDataLoading) return;
    if (
      window.location.pathname === '/passport' &&
      !isPassportMode &&
      user &&
      (userShops?.length || 0) > 0
    ) {
      window.history.replaceState(null, '', '/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isShopDataLoading]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
      <TooltipProvider>
      <NotificationProvider>
        <AuthProvider>
          <TrainingMonitoringConsentProvider>
          <TrainingLaunchDisclosureProvider>
          <CompanionRehydrateBridge />
          <IdleSignOutGuard />
          <MobileTrainingReturnPrompt />
          <SupportModeProvider>
            <TimezoneProvider>
              <TutorialProvider>
                <MarketingModeProvider>
                    <ScopeExpansionHandler>
                      <DateProvider>
                      {/* CORE-BP item 6: one home for the post-add suggestion
                          question, so the six paths that can add work to an
                          existing ticket cannot each grow their own. */}
                      <AddonSuggestionProvider>
                  <UpdateNotification />
                  <AppUpdateRequiredBanner />
                  <Toaster />
                <Sonner />
                <StormforgeOverlay />
                <StormforgeListener />
                <USOnlyNotice />
                <BetaConfidentialityOverlay />
                <BrowserRouter>
                  <BootRouteReset />
                  <SupportModeBanner />
                  <SupportModeNavigationTracker />
                  {/* Report Issue Button - inside Router for useLocation */}
                  <ReportIssueButton />
                  {/* OCT20C-F1: keeps --floating-controls-reserve current; <main> below keeps that band clear. */}
                  <FloatingControlsReserveHost />
                  {/* The time review after a Stop that completed its ticket —
                      the card that asked leaves the board. */}
                  <TimerReviewHost />
                {/* Route-aware tutorial watcher ensures bar follows navigation */}
                <TutorialRouteWatcher />
                {/* Global tutorial bar persists across all routes */}
                <GlobalTutorialBar />
                <TrainingCompletionReturnPromptProvider>
                <main className="app-floating-reserve">
                <Routes>
                  {/* Public routes - outside BillingGate */}
                  <Route path="/auth/confirm" element={<AuthConfirm />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/onboard" element={<Onboard />} />
                  <Route path="/onboard/shop" element={<OnboardShop />} />
                  <Route path="/onboard/setup" element={<OnboardSetup />} />
                  {/* Slice 2.5 — Historical Import flow. All four routes
                      enforce importEnabledForInternal at the page level
                      and redirect back to /onboard/setup when the flag
                      is off. Order matters: more specific routes first. */}
                  <Route path="/onboard/setup/import" element={<OnboardSetupImport />} />
                  <Route path="/onboard/setup/import/:runId/preview" element={<OnboardSetupImportPreview />} />
                  <Route path="/onboard/setup/import/:runId/inferences" element={<OnboardSetupImportInferences />} />
                  <Route path="/onboard/setup/import/:runId/wizard" element={<OnboardSetupImportWizard />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />
                  <Route path="/join-team" element={<JoinTeam />} />
                  <Route path="/join-team/accept" element={<JoinTeamAccept />} />
                  <Route path="/beta" element={<BetaInvite />} />
                  <Route path="/beta/complete" element={<BetaComplete />} />
                  <Route path="/billing/success" element={<BillingSuccess />} />
                  {/* OptiQueue - Public booking page */}
                  <Route path="/book/:shopSlug" element={<OptiQueueBookingPage />} />
                  {/* OptiDVI - Public customer view */}
                  <Route path="/dvi/view/:token" element={<DviCustomerView />} />
                  {/* OptiChronicle - Customer portal (public, not staff-gated) */}
                  <Route path="/chronicle/*" element={<ExposureRouteGate feature="ui_chronicle"><ChronicleRouter /></ExposureRouteGate>} />
                  {/* Legal pages - public, no auth required */}
                  <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                  {/* Core-100 R2 (CORE-N): a reference answers or withdraws; no account needed. */}
                  <Route path="/reference/respond" element={<ReferenceRespond />} />
                  <Route path="/legal/terms" element={<TermsOfService />} />
                  <Route path="/legal/passport-terms" element={<PassportTerms />} />
                  <Route path="/legal/beta-agreement" element={<BetaAgreement />} />

                  {/* Beta Agreement Signing — auth-protected, billing-gate-safe */}
                  <Route path="/beta/sign-agreement" element={
                    <ProtectedRoute>
                      <BetaSignAgreement />
                    </ProtectedRoute>
                  } />

                  {/* Beta Legal Review Hub — auth-protected, billing-gate-safe */}
                  <Route path="/beta/legal-review" element={
                    <ProtectedRoute>
                      <BetaLegalReview />
                    </ProtectedRoute>
                  } />
                  
                  {/* Core-100 direction 10: request a creator or school account (signed in, no shop needed) */}
                  <Route path="/request-account" element={
                    <ProtectedRoute passportAllowed>
                      <RequestAccount />
                    </ProtectedRoute>
                  } />

                  {/* Creator Impact Portal - Separate auth surface */}
                  <Route path="/creator/auth" element={<CreatorAuth />} />
                  <Route path="/creator" element={
                    <CreatorProtectedRoute>
                      <CreatorDashboard />
                    </CreatorProtectedRoute>
                  } />
                
                  {/* Protected routes - wrapped in BillingGate */}
                  <Route path="/*" element={
                    <BillingGate>
                      <Routes>
                        {/* Alpha Testing Management - Manager Only */}
                        <Route 
                          path="/alpha-testers" 
                          element={
                            <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                              <AlphaTesters />
                            </ProtectedRoute>
                          } 
                        />

                        {/* Organization View - OWNER/ORG_ADMIN/MANAGER (scope-gated inside page) */}
                        <Route
                          path="/org"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <OrgView />
                            </ProtectedRoute>
                          }
                        />

                        {/* Cross-Shop Intelligence - same access as /org, scope-gated inside page */}
                        <Route
                          path="/org/intelligence"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <OrgIntelligence />
                            </ProtectedRoute>
                          }
                        />

                        {/* Cross-Shop Recommendations - same access as /org, scope-gated inside page */}
                        <Route
                          path="/org/recommendations"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <OrgRecommendations />
                            </ProtectedRoute>
                          }
                        />

                        {/* Round 73 — Floo-Aware Talent Directory (in-scope) */}
                        <Route
                          path="/enterprise/talent"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <EnterpriseTalent />
                            </ProtectedRoute>
                          }
                        />
                        {/* Round 74 — Scope Talent Analytics (read-only, A-C-04) */}
                        <Route
                          path="/enterprise/talent/analytics"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <EnterpriseTalentAnalytics />
                            </ProtectedRoute>
                          }
                        />
                        {/* Round 75 — Floo-Aware Training Visibility (A-C-05) */}
                        <Route
                          path="/enterprise/training"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <EnterpriseTraining />
                            </ProtectedRoute>
                          }
                        />
                        {/* Round 78 — Cross-Location Fit Suggestions (A-C-09) */}
                        <Route
                          path="/enterprise/talent/suggestions"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <EnterpriseTalentSuggestions />
                            </ProtectedRoute>
                          }
                        />
                        {/* Round 80 — Intelligence Governance / Methodology (A-C-12) */}
                        <Route
                          path="/enterprise/intelligence/governance"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <EnterpriseIntelligenceGovernance />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/enterprise/talent/:userId"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <TalentProfileView />
                            </ProtectedRoute>
                          }
                        />

                        {/* OptiNetwork (Stage 2) — hub/landing page.
                            User-facing route. Cards on this page link out to
                            the Map, Company Dashboard, intelligence surfaces,
                            and talent/training views. Role gating mirrors the
                            legacy /admin/floo-network surface (OWNER /
                            ORG_ADMIN / MANAGER); the hub itself renders only
                            the cards the user can actually open. */}
                        <Route
                          path="/admin/optinetwork"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <OptiNetworkDashboard />
                            </ProtectedRoute>
                          }
                        />

                        {/* OptiNetwork Map — moved to nested path in Stage 2.
                            Same component, same protection as the legacy
                            /admin/floo-network entry. */}
                        <Route
                          path="/admin/optinetwork/map"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <FlooNetworkMap />
                            </ProtectedRoute>
                          }
                        />

                        {/* Legacy compatibility — /admin/floo-network redirects
                            to the new /admin/optinetwork/map. Stage 1 kept this
                            route as the primary user-facing entry; Stage 2
                            renames it but preserves the path so external links
                            and bookmarks keep working. Wrapped in ProtectedRoute
                            so unauthenticated users still hit the auth flow
                            before redirect, and so the p9 §J doctrinal scan
                            (asserts path="/admin/floo-network" with
                            OWNER/ORG_ADMIN/MANAGER roles) still passes.
                            `replace` so the legacy URL doesn't accumulate in
                            history. */}
                        <Route
                          path="/admin/floo-network"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER']}>
                              <Navigate to="/admin/optinetwork/map" replace />
                            </ProtectedRoute>
                          }
                        />

                        {/* Floo Network Phase 7 — Region / District dashboards.
                            Route guards admit the five management roles that
                            could plausibly view a region or district. Page-level
                            access is verified inside each component against the
                            user's `useFlooScopeOptions` allowed-option set —
                            shop-only MANAGERs will land on the page and see an
                            Access Denied card. */}
                        <Route
                          path="/org/regions/:regionId"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER', 'REGION_MANAGER', 'DISTRICT_MANAGER']}>
                              <RegionDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/org/districts/:districtId"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'ORG_ADMIN', 'MANAGER', 'REGION_MANAGER', 'DISTRICT_MANAGER']}>
                              <DistrictDashboard />
                            </ProtectedRoute>
                          }
                        />

                        {/* Passport Mode landing page */}
                        <Route
                          path="/passport"
                          element={
                            <ProtectedRoute passportAllowed>
                              <PassportHome />
                            </ProtectedRoute>
                          }
                        />

                        {/* OptiBay Connect */}
                        <Route
                          path="/connect"
                          element={
                            <ProtectedRoute passportAllowed>
                              <ConnectPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Talent Profile View (Connect) */}
                        <Route
                          path="/connect/talent/:userId"
                          element={
                            <ProtectedRoute passportAllowed>
                              <TalentProfileView />
                            </ProtectedRoute>
                          }
                        />

                        {/* School Portal */}
                        <Route
                          path="/school"
                          element={
                            <ProtectedRoute passportAllowed>
                              <SchoolRoute>
                                <SchoolPortal />
                              </SchoolRoute>
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<SchoolDashboard />} />
                          <Route path="programs" element={<ProgramsManager />} />
                          <Route path="cohorts" element={<CohortsManager />} />
                          <Route path="requests" element={<JoinRequestsQueue />} />
                          <Route path="attestations" element={<StudentVerificationsManager />} />
                          <Route path="staff" element={<StaffManager />} />
                        </Route>

                        {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN']} requiredWorkRoles={['DETAIL_SPECIALIST']} forbiddenRedirectTo="/schedule-conflicts">
                      <ReminderTriggerProvider><Dashboard /></ReminderTriggerProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shop-profile"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <ShopProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/team-management"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <TeamManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/technicians"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <Technicians />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/technicians/:id/edit"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <TechnicianEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/technicians/:id"
                  element={
                    // OCT20A P2: an ORG_ADMIN of the shop's organisation views the Passport read-only — the server
                    // (can_view_technician_passport) decides; the page renders "not found" to anyone it refuses.
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN', 'ORG_ADMIN']} requiredWorkRoles={['DETAIL_SPECIALIST']}>
                      <TechnicianPassport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shop-floor"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <ReminderTriggerProvider><ShopFloor /></ReminderTriggerProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shop-floor/editor"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <FacilityEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sync-vehicle-data"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <SyncVehicleData />
                    </ProtectedRoute>
                  }
                />
                {/* FIX-R131-HIGH-1: vehicle_catalog is a GLOBAL cross-tenant
                    lookup table. Writes are restricted to internal_admin via
                    RLS (migration 20260425150500). Match the UI gate so
                    non-admin managers don't see a page that will silently
                    fail when they try to import. */}
                <Route
                  path="/import-vehicle-catalog"
                  element={
                    <ProtectedRoute>
                      <InternalAdminRoute>
                        <ImportVehicleCatalog />
                      </InternalAdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import-geo-vehicles"
                  element={
                    <ProtectedRoute>
                      <InternalAdminRoute>
                        <ImportGeoVehicles />
                      </InternalAdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <ImportJobs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import-sources"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <ImportSources />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import-runs"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <ImportRuns />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/birdseye"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN']} requiredWorkRoles={['DETAIL_SPECIALIST']}>
                      <ReminderTriggerProvider><Birdseye /></ReminderTriggerProvider>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deleted-tickets"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <DeletedTickets />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/completed-tickets"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <CompletedTickets />
                    </ProtectedRoute>
                  }
                />
                {/* ORG_ADMIN added (Batch 3 — FOH Timer Handoff) so org
                    admins can reach the shop-scoped Advisor Performance
                    analytics for their active shop. */}
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN']}>
                      <Analytics />
                    </ProtectedRoute>
                  }
                />
                {/* CORE-BQ Slice B (Definition of Done item 2): the shop-facing
                    integrity surface. Read-only, and gated to the roles that can
                    act on a contradiction. */}
                <Route
                  path="/operational-integrity"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'ORG_ADMIN']}>
                      <OperationalIntegrity />
                    </ProtectedRoute>
                  }
                />
                {/* DEV ONLY: Assignment Score Inspector */}
                {import.meta.env.DEV && (
                  <Route
                    path="/assignment-debug"
                    element={
                      <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                        <AssignmentDebug />
                      </ProtectedRoute>
                    }
                  />
                )}
                <Route
                  path="/account-settings"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN']} requiredWorkRoles={['DETAIL_SPECIALIST']} passportAllowed>
                      <AccountSettings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/create-ticket"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <CreateTicket />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/specialty-bays"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <SpecialtyBays />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/beta-leads"
                  element={
                    <InternalAdminRoute>
                      <BetaLeads />
                    </InternalAdminRoute>
                  }
                />

                <Route
                  path="/feature-flags"
                  element={
                    <InternalAdminRoute>
                      <FeatureFlagsAdmin />
                    </InternalAdminRoute>
                  }
                />

                {/* BJ-GAP-002: governing the support-operator set is an
                    INTERNAL ADMIN power. Deliberately not SupportOperatorRoute
                    — otherwise a support operator could grant support. */}
                <Route
                  path="/admin/support-operators"
                  element={
                    <InternalAdminRoute>
                      <SupportOperatorsAdmin />
                    </InternalAdminRoute>
                  }
                />

                {/* CORE-BK responsibility 9 — internal control posture.
                    InternalAdminRoute, not a shop role gate: this is an
                    operational view of the platform, not a shop surface. */}
                <Route
                  path="/admin/privacy-posture"
                  element={
                    <InternalAdminRoute>
                      <PrivacyPosture />
                    </InternalAdminRoute>
                  }
                />

                {/* Developer-only demo seeder (Bob Ford POC). Internal admin
                    only + additionally gated inside the page by
                    VITE_ENABLE_DEMO_SEEDER. No nav entry — URL access only. */}
                {/* Admin-only Quality Command Center — read-only QE Platform V2
                    evidence. Route-gated here AND re-checked inside the page. */}
                <Route
                  path="/admin/quality"
                  element={
                    <InternalAdminRoute>
                      <QualityCommandCenter />
                    </InternalAdminRoute>
                  }
                />
                <Route
                  path="/admin/quality/runs/:runId"
                  element={
                    <InternalAdminRoute>
                      <QualityRunDetail />
                    </InternalAdminRoute>
                  }
                />

                <Route
                  path="/admin/demo-seeder"
                  element={
                    <InternalAdminRoute>
                      <DemoSeeder />
                    </InternalAdminRoute>
                  }
                />

                {/* Internal-admin-only test/demo/dev data cleanup tool.
                    Talks to the admin-cleanup-test-data edge function; runs
                    audit / dry-run / cleanup against whichever Supabase
                    project the app is connected to. Additionally gated by
                    VITE_ENABLE_TEST_DATA_CLEANUP inside the page. No nav. */}
                <Route
                  path="/admin/test-data-cleanup"
                  element={
                    <InternalAdminRoute>
                      <TestDataCleanup />
                    </InternalAdminRoute>
                  }
                />

                {/* Manual Batch 1: added ORG_ADMIN (can already reach
                    Analytics / OptiAssets / FOH queue, so should be able to
                    read the guide). AUDITOR intentionally deferred. */}
                <Route
                  path="/user-guide"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN', 'ORG_ADMIN']} requiredWorkRoles={['DETAIL_SPECIALIST']}>
                      <UserGuide />
                    </ProtectedRoute>
                  }
                />

                {/* Lifecycle v2 — slice 4A. Flag-gated inside the page; route */}
                {/* additionally role-gates to FOH-capable roles per spec §6.1. */}
                {/* WS-GAPS2 B-W11 (LEDGER W4d-3 + D-1b): the schedule conflicts approvers AND operational dispatchers */}
                {/* are notified about, READ-ONLY. Gated by the server audience predicate is_schedule_conflict_viewer, */}
                {/* not a role list; grants no decision, assignment or write. */}
                <Route
                  path="/schedule-conflicts"
                  element={
                    <ProtectedRoute>
                      <ScheduleConflictViewerRoute>
                        <ScheduleConflicts />
                      </ScheduleConflictViewerRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/foh-queue"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN']}>
                      <FohQueue />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/addon-suggestion-settings"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER', 'ORG_ADMIN']}>
                      <AddonSuggestionSettings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/vehicle-duplicates"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER', 'ORG_ADMIN']}>
                      <VehicleDuplicates />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dvi-intelligence"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <ExposureRouteGate feature="ui_evidenceDiagnostics">
                        <DviIntelligenceDashboard />
                      </ExposureRouteGate>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dvi-learning"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <DviLearningMetricsDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/bug-reports"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <BugReports />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/bug-reports/:id"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <BugReportDetail />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/support/shops"
                  element={
                    <SupportOperatorRoute>
                      <SupportShops />
                    </SupportOperatorRoute>
                  }
                />

                <Route
                  path="/support/dashboard"
                  element={
                    <SupportOperatorRoute>
                      <SupportDashboard />
                    </SupportOperatorRoute>
                  }
                />

                <Route
                  path="/support/granted-shop"
                  element={
                    <SupportOperatorRoute>
                      <SupportGrantedShopView />
                    </SupportOperatorRoute>
                  }
                />

                {/* V-F-1d: Internal/Admin only — vendor jobs-catalog AI review.
                    Reads global cross-tenant `jobs_catalog_bay_staging` table. */}
                <Route
                  path="/bay-rules-review"
                  element={
                    <InternalAdminRoute>
                      <BayRulesReview />
                    </InternalAdminRoute>
                  }
                />

                {/* V-F-1d: Internal/Admin only — vendor training-catalog admin.
                    Page-level `useIsInternalAdmin` was already authoritative;
                    this route-level wrap consolidates the pattern with
                    sister `/admin/*` routes and removes the redundant
                    `learningCenter_catalogAdmin` flag dance. */}
                <Route
                  path="/training-catalog"
                  element={
                    <InternalAdminRoute>
                      <TrainingCatalogAdmin />
                    </InternalAdminRoute>
                  }
                />

                {/* TCX-7 — Training domain-match policy admin. Internal-only;
                    route-gated by InternalAdminRoute (the page also re-checks
                    useIsInternalAdmin defense-in-depth). Not surfaced in nav. */}
                <Route
                  path="/training-domain-policy"
                  element={
                    <InternalAdminRoute>
                      <TrainingDomainPolicyAdmin />
                    </InternalAdminRoute>
                  }
                />

                {/* V-OCIS-Intake-4A: Internal-only AI Intake Lab — text-only
                    workbench for the deployed OCIS AI Intake pipeline
                    (intake-create → extract-intake → approve-intake). Route
                    gated by InternalAdminRoute; component additionally
                    requires the `aiIntakeEnabled` feature flag for the
                    active shop (defense-in-depth). Not surfaced in nav. */}
                <Route
                  path="/ai-intake-lab"
                  element={
                    <InternalAdminRoute>
                      <AiIntakeLab />
                    </InternalAdminRoute>
                  }
                />

                {/* Diagnostic Flow Admin - Manager Only, Catalyst Feature */}
                <Route
                  path="/diagnostic-flows"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <ExposureRouteGate feature="ui_workflowDiagnostics">
                        <DiagnosticFlowAdmin />
                      </ExposureRouteGate>
                    </ProtectedRoute>
                  }
                />

                {/* Inspection Template Editor - Manager Only, requires OPTIDVI add-on */}
                <Route
                  path="/inspection-templates"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'ORG_ADMIN']}>
                      <ExposureRouteGate feature="ui_workflowInspections">
                        <InspectionTemplates />
                      </ExposureRouteGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inspection-templates/:templateId"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'ORG_ADMIN']}>
                      <ExposureRouteGate feature="ui_workflowInspections">
                        <InspectionTemplates />
                      </ExposureRouteGate>
                    </ProtectedRoute>
                  }
                />

                {/* OptiQueue Settings - Manager Only; OptiQueue is Core, shown while the shop has it switched on */}
                <Route
                  path="/optiqueue-settings"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <OptiQueueSettings />
                    </ProtectedRoute>
                  }
                />

                {/* OptiAssets — view roles must match usePermissions.canViewAssets
                    (OWNER / MANAGER / AUDITOR / ORG_ADMIN). TECHNICIAN and
                    SERVICE_WRITER are intentionally excluded for now: there is
                    no read-only OptiAssets UX for them, and the previous wider
                    role list let them through the route only to be bounced by
                    OptiAssetsGate. Parity is pinned by
                    tests/unit/optiassets/optiAssetsBatch1Consistency. */}
                <Route
                  path="/optiassets"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN']}>
                      <ExposureRouteGate feature="ui_optiAssets">
                        <OptiAssets />
                      </ExposureRouteGate>
                    </ProtectedRoute>
                  }
                />

                {/* V-F-1d: Payroll Reports = OptiShop scope (financial), hidden
                    from Core beta via `ui_payroll_reports` (default OFF). Role
                    gate retained as defense-in-depth for the eventual
                    OptiShop-tier flip. */}
                <Route
                  path="/payroll-reports"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <ExposureRouteGate feature="ui_payroll_reports">
                        <PayrollReports />
                      </ExposureRouteGate>
                    </ProtectedRoute>
                  }
                />

                {/* Role Intelligence Passports */}
                <Route
                  path="/advisor-passport/:userId"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <AdvisorPassport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manager-passport/:userId"
                  element={
                    <ProtectedRoute requiredRoles={['MANAGER', 'OWNER']}>
                      <ManagerPassport />
                    </ProtectedRoute>
                  }
                />

                {/* Customer Profiles */}
                <Route
                  path="/customers"
                  element={
                    // OD-34: technicians and detail specialists READ the shop's customers (read-only for them —
                    // creating tickets / editing contact stays front office, enforced by the server).
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN']} requiredWorkRoles={['DETAIL_SPECIALIST']}>
                      <PageWithTopBar>
                        <Customers />
                      </PageWithTopBar>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:id"
                  element={
                    // OD-34 — as /customers above.
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN']} requiredWorkRoles={['DETAIL_SPECIALIST']}>
                      <PageWithTopBar>
                        <CustomerProfile />
                      </PageWithTopBar>
                    </ProtectedRoute>
                  }
                />

                {/* OptiBoard - Ticket Lifecycle Management */}
                <Route
                  path="/optiboard"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <OptiBoard />
                    </ProtectedRoute>
                  }
                />

                {/* Customer Inbox - OCIS Triage View */}
                <Route
                  path="/inbox"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER']}>
                      <ExposureRouteGate feature="ui_customerInbox">
                        <CustomerInbox />
                      </ExposureRouteGate>
                    </ProtectedRoute>
                  }
                />

                {/* Attach Evidence - Mobile share-sheet handoff */}
                <Route
                  path="/attach"
                  element={
                    <ProtectedRoute requiredRoles={['OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN']} requiredWorkRoles={['DETAIL_SPECIALIST']}>
                      <AttachEvidence />
                    </ProtectedRoute>
                  }
                />

                {/* V-F-1d: Internal/Admin only — was previously auth-only
                    (gating gap noted in V-F-1b). Now wrapped in
                    InternalAdminRoute to match sister `/admin/*` routes. */}
                <Route
                  path="/admin/decoupled-users"
                  element={
                    <InternalAdminRoute>
                      <DecoupledUsers />
                    </InternalAdminRoute>
                  }
                />

                {/* Admin: Beta Control Panel (internal admin only) */}
                <Route
                  path="/admin/beta"
                  element={
                    <InternalAdminRoute>
                      <BetaControlPanel />
                    </InternalAdminRoute>
                  }
                />

                {/* Core-100 direction 10: account requests (internal admin only) */}
                <Route
                  path="/admin/account-requests"
                  element={
                    <InternalAdminRoute>
                      <AccountRequestsAdmin />
                    </InternalAdminRoute>
                  }
                />

                {/* Admin: School Creator (internal admin only) */}
                <Route
                  path="/admin/schools"
                  element={
                    <InternalAdminRoute>
                      <AdminSchoolCreator />
                    </InternalAdminRoute>
                  }
                />

                        {/* V-F-1d: Phase 7 R1 — Purchasing = OptiShop scope.
                            Hidden from Core beta via `ui_purchasing`
                            (default OFF). */}
                        <Route
                          path="/purchasing"
                          element={
                            <ProtectedRoute>
                              <ExposureRouteGate feature="ui_purchasing">
                                <PurchaseOrderListPage />
                              </ExposureRouteGate>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/purchasing/:id"
                          element={
                            <ProtectedRoute>
                              <ExposureRouteGate feature="ui_purchasing">
                                <PurchaseOrderDetailPage />
                              </ExposureRouteGate>
                            </ProtectedRoute>
                          }
                        />

                        {/* V-F-1d: Phase 8 R2 — Canned Jobs management =
                            OptiShop scope. Hidden from Core beta via
                            `ui_canned_jobs` (default OFF). */}
                        <Route
                          path="/canned-jobs"
                          element={
                            <ProtectedRoute requiredRoles={['OWNER', 'MANAGER']}>
                              <ExposureRouteGate feature="ui_canned_jobs">
                                <CannedJobsPage />
                              </ExposureRouteGate>
                            </ProtectedRoute>
                          }
                        />

                        {/* Phase 9 R2 — Inventory admin (auth-only; gating in page) */}
                        <Route
                          path="/inventory"
                          element={
                            <ProtectedRoute>
                              <InventoryPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Phase 11 — Customer Invoicing & Estimates */}
                        <Route
                          path="/invoices"
                          element={
                            <ProtectedRoute>
                              <ExposureRouteGate feature="ui_customer_invoices">
                                <InvoicesList />
                              </ExposureRouteGate>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/invoices/:invoiceId"
                          element={
                            <ProtectedRoute>
                              <ExposureRouteGate feature="ui_customer_invoices">
                                <InvoiceDetail />
                              </ExposureRouteGate>
                            </ProtectedRoute>
                          }
                        />

                        {/* Catch-all */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </BillingGate>
                  } />
                </Routes>
                </main>
                </TrainingCompletionReturnPromptProvider>
                <FeatureFlagsDebug />
                <EveVoiceShellWithActiveShop />
              </BrowserRouter>
              </AddonSuggestionProvider>
            </DateProvider>
          </ScopeExpansionHandler>
        </MarketingModeProvider>
      </TutorialProvider>
    </TimezoneProvider>
  </SupportModeProvider>
          </TrainingLaunchDisclosureProvider>
          </TrainingMonitoringConsentProvider>
</AuthProvider>
</NotificationProvider>
    </TooltipProvider>
    </ErrorBoundary>
  </QueryClientProvider>
);

export default App;
